import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './TicketSuccess.css';

const THEMES = {
  amphitheater: {
    bg: '#f5f3ff',
    accent: '#7c3aed',
    border: '#c4b5fd',
    gradient: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
    badge: 'AMPHITHEATER',
    title: 'Concert Entry Granted',
  },
  festival_3day: {
    bg: '#ecfdf5',
    accent: '#059669',
    border: '#6ee7b7',
    gradient: 'linear-gradient(135deg, #059669, #10b981)',
    badge: '3-DAY PASS',
    title: 'Festival Access Granted',
  },
  festival_2day: {
    bg: '#eff6ff',
    accent: '#2563eb',
    border: '#93c5fd',
    gradient: 'linear-gradient(135deg, #2563eb, #3b82f6)',
    badge: '2-DAY PASS',
    title: 'Festival Access Granted',
  },
  festival_1day: {
    bg: '#fffbeb',
    accent: '#d97706',
    border: '#fcd34d',
    gradient: 'linear-gradient(135deg, #d97706, #f59e0b)',
    badge: '1-DAY PASS',
    title: 'Festival Access Granted',
  },
  vendor: {
    bg: '#faf5ff',
    accent: '#9333ea',
    border: '#c084fc',
    gradient: 'linear-gradient(135deg, #9333ea, #a855f7)',
    badge: 'VENDOR',
    title: 'Vendor Access Granted',
  },
  comp: {
    bg: '#f0fdf4',
    accent: '#16a34a',
    border: '#86efac',
    gradient: 'linear-gradient(135deg, #16a34a, #22c55e)',
    badge: 'COMPLIMENTARY',
    title: 'Festival Access Granted',
  },
  parking: {
    bg: '#fef3c7',
    accent: '#b45309',
    border: '#fcd34d',
    gradient: 'linear-gradient(135deg, #b45309, #f59e0b)',
    badge: 'VIP PARKING PASS',
    title: 'Parking Access Granted',
  },
  default: {
    bg: '#f8fafc',
    accent: '#16a34a',
    border: '#d1d5db',
    gradient: 'linear-gradient(135deg, #16a34a, #22c55e)',
    badge: 'TICKET',
    title: 'Entry Granted',
  },
};

const USED_THEME = {
  bg: '#e2e8f0',
  accent: '#334155',
  border: '#94a3b8',
  gradient: 'linear-gradient(135deg, #475569, #64748b)',
  badge: 'ALREADY USED',
  title: 'Ticket Already Used',
};

const SCANNED_TODAY_THEME = {
  bg: '#fffbeb',
  accent: '#b45309',
  border: '#fcd34d',
  gradient: 'linear-gradient(135deg, #d97706, #f59e0b)',
  badge: 'COME BACK NEXT DAY',
  title: 'Already Entered Today',
};

const FESTIVAL_DATE_LABELS = {
  '2026-06-19': 'June 19, 2026',
  '2026-06-20': 'June 20, 2026',
  '2026-06-21': 'June 21, 2026',
};
const FESTIVAL_DATE_RANGE_LABEL = 'June 19-21, 2026';

const DATE_TEXT_REGEX = /\b(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},\s+\d{4}\b/gi;

function parseDateValue(rawValue) {
  if (!rawValue) return null;
  if (rawValue instanceof Date && !Number.isNaN(rawValue.getTime())) return rawValue;

  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim();
    const exactDateMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (exactDateMatch) {
      const [, year, month, day] = exactDateMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  return null;
}

function formatDateTime(rawValue) {
  const parsed = parseDateValue(rawValue);
  if (!parsed) return rawValue || 'N/A';
  return parsed.toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
}

function getIsoDate(rawValue) {
  const parsed = parseDateValue(rawValue);
  if (!parsed) return null;
  const year = parsed.getFullYear();
  const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
  const day = `${parsed.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatEventDate(rawValue) {
  const iso = getIsoDate(rawValue);
  if (iso && FESTIVAL_DATE_LABELS[iso]) {
    return FESTIVAL_DATE_LABELS[iso];
  }

  const parsed = parseDateValue(rawValue);
  if (!parsed) return rawValue || '';

  return parsed.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function sanitizePassType(rawPassType, isAmp) {
  if (!rawPassType) return 'N/A';

  let normalized = rawPassType
    .replace(/\s*\(Complimentary[^)]*\)/gi, '')
    .replace(/\s*\(comp[^)]*\)/gi, '')
    .trim();

  if (isAmp) {
    normalized = normalized.replace(DATE_TEXT_REGEX, '').replace(/\s{2,}/g, ' ').trim();
    normalized = normalized.replace(/\s*-\s*$/, '').trim();
  }

  return normalized || rawPassType;
}

function resolveScanImage({ isAmp, isVendor, eventDateRaw, checkInTime, passType }) {
  if (passType && /parking/i.test(passType)) return null;

  const iso = getIsoDate(eventDateRaw) || getIsoDate(checkInTime);

  if (isAmp) {
    if (iso === '2026-06-20') return '/scanning_images/PAC AMP - Saturday.png';
    if (iso === '2026-06-21') return '/scanning_images/PAC AMP - Sunday.png';
    return '/scanning_images/PAC AMP - Saturday.png';
  }

  const festivalIso = iso;
  if (festivalIso === '2026-06-19') return '/scanning_images/Festival - Friday.png';
  if (festivalIso === '2026-06-20') return '/scanning_images/Festival - Saturday.png';
  if (festivalIso === '2026-06-21') return '/scanning_images/Festival - Sunday.png';

  if (isVendor) return '/scanning_images/Vendors.png';

  return null;
}

function detectTheme(ticketData) {
  const passType = (ticketData.passType || ticketData.ticketData?.ticket_type || '').toLowerCase();
  const businessType = (ticketData.businessType || ticketData.ticketData?.business_type || '').toLowerCase();

  if (ticketData.isAmphitheater || passType.includes('amphitheater') || passType.includes('pac amp')) return 'amphitheater';

  if (passType.includes('vendor') || businessType) return 'vendor';

  if (passType.includes('parking')) return 'parking';

  const days = ticketData.totalDays;
  if (days === 3 || passType.includes('3-day') || passType.includes('3 day')) return 'festival_3day';
  if (days === 2 || passType.includes('2-day') || passType.includes('2 day')) return 'festival_2day';
  if (days === 1 || passType.includes('1-day') || passType.includes('1 day')) return 'festival_1day';

  return 'default';
}

export const buildTicketSuccessState = (resultData = {}, options = {}) => ({
  ticketData: resultData,
  checkInTime: options.checkInTime || new Date().toISOString(),
  passType: resultData.ticket_type || 'N/A',
  holderName: resultData.owner_name || 'N/A',
  status: resultData.status || 'VALID',
  isAmphitheater: resultData.is_amphitheater || false,
  section: resultData.section,
  row: resultData.row,
  seat: resultData.seat,
  eventDate: resultData.event_date || resultData.scan_event_date,
  eventName: resultData.event_name,
  totalDays: resultData.total_days,
  dayNumber: resultData.day_number,
  validForDate: resultData.valid_for_date,
  businessType: resultData.business_type,
  boothName: resultData.booth_name,
  ownerEmail: resultData.owner_email,
  scanMode: resultData.scan_mode || 'both',
  ampScanned: resultData.amp_scanned,
  festivalScanned: resultData.festival_scanned,
  usedAt: resultData.used_at,
  scannedBy: resultData.scanned_by,
  scannedAt: resultData.scanned_at,
  alreadyUsed: String(resultData.status || '').toUpperCase() === 'ALREADY_USED',
  message: resultData.message,
});

export const TicketSuccessCard = ({
  ticketData = {},
  onScanNext,
  onClose,
  preview = false,
  compact = false,
}) => {

  const themeKey = detectTheme(ticketData);
  const theme = THEMES[themeKey] || THEMES.default;
  const rawPassType = ticketData.passType || ticketData.ticketData?.ticket_type || 'N/A';
  const isAmp = themeKey === 'amphitheater';
  const messageText = String(ticketData.message || '');
  const isSameDayGroupBlock = /pass group already scanned today|already entered today/i.test(messageText);
  const isAlreadyUsed = ticketData.alreadyUsed || String(ticketData.status || '').toUpperCase() === 'ALREADY_USED';
  const isScannedToday = String(ticketData.status || '').toUpperCase() === 'SCANNED_TODAY';
  const isComeBackNextDay = isScannedToday || (isAlreadyUsed && isSameDayGroupBlock);
  const isHardBlocked = isAlreadyUsed && !isComeBackNextDay;
  const isBlocked = isHardBlocked || isComeBackNextDay;
  const activeTheme = isHardBlocked ? USED_THEME : (isComeBackNextDay ? SCANNED_TODAY_THEME : theme);
  const rawPassTypeLower = rawPassType.toLowerCase();
  const isVendorCompPass = rawPassTypeLower.includes('vendor') && rawPassTypeLower.includes('comp');
  const passType = isVendorCompPass ? '3-Day Comp Pass' : sanitizePassType(rawPassType, isAmp);
  const isFestivalRangeTicket = !isAmp && (
    themeKey.startsWith('festival_')
    || /(?:^|\b)(?:1|2|3)\s*[- ]day(?:\b|\s*pass)/i.test(rawPassType)
    || rawPassTypeLower.includes('festival pass')
  );
  const eventDateRaw = ticketData.eventDate || ticketData.ticketData?.scan_event_date || ticketData.validForDate || ticketData.usedAt || ticketData.checkInTime;
  const eventDateLabel = isFestivalRangeTicket
    ? FESTIVAL_DATE_RANGE_LABEL
    : formatEventDate(eventDateRaw);
  const isVendor = themeKey === 'vendor' || isVendorCompPass;
  const scanImage = isBlocked ? null : resolveScanImage({
    isAmp,
    isVendor,
    eventDateRaw,
    checkInTime: ticketData.checkInTime,
    passType: rawPassType,
  });
  const scannedAtText = ticketData.scannedAt ? formatDateTime(ticketData.scannedAt) : '';
  const usedAtText = ticketData.usedAt ? formatDateTime(ticketData.usedAt) : '';

  useEffect(() => {
    if (preview) return undefined;

    const previousBodyBg = document.body.style.backgroundColor;
    const previousHtmlBg = document.documentElement.style.backgroundColor;
    const previousBodyOverscroll = document.body.style.overscrollBehaviorY;
    const previousHtmlOverscroll = document.documentElement.style.overscrollBehaviorY;

    document.body.style.backgroundColor = activeTheme.bg;
    document.documentElement.style.backgroundColor = activeTheme.bg;
    document.body.style.overscrollBehaviorY = 'none';
    document.documentElement.style.overscrollBehaviorY = 'none';

    return () => {
      document.body.style.backgroundColor = previousBodyBg;
      document.documentElement.style.backgroundColor = previousHtmlBg;
      document.body.style.overscrollBehaviorY = previousBodyOverscroll;
      document.documentElement.style.overscrollBehaviorY = previousHtmlOverscroll;
    };
  }, [activeTheme.bg, preview]);

  const pageStyle = preview
    ? {
      backgroundColor: activeTheme.bg,
      minHeight: 'auto',
      height: 'auto',
      borderRadius: 14,
      overflow: 'hidden',
    }
    : { backgroundColor: activeTheme.bg };

  const contentStyle = preview
    ? {
      padding: compact ? '18px 12px 12px' : '20px 14px 14px',
      justifyContent: 'flex-start',
    }
    : undefined;

  const ticketInfoStyle = {
    borderColor: activeTheme.border,
    maxWidth: compact ? 300 : 320,
    marginBottom: preview ? 12 : 28,
  };

  const handleScanNext = onScanNext || (() => {});
  const handleClose = onClose || (() => {});

  return (
    <div className={`success-page ${isHardBlocked ? 'is-used' : ''}`} style={pageStyle}>

      <div className="success-content" style={contentStyle}>
        <div className="success-top-row" style={preview ? { justifyContent: 'center' } : undefined}>
          <div className="success-icon">
            <div className={`success-check-ring ${isHardBlocked ? 'is-failed' : ''}`} style={{ borderColor: isHardBlocked ? '#dc2626' : (isComeBackNextDay ? '#d97706' : '#16a34a') }}>
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                {isHardBlocked ? (
                  <>
                    <path d="M11 11L25 25" stroke="#dc2626" strokeWidth="3.8" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M25 11L11 25" stroke="#dc2626" strokeWidth="3.8" strokeLinecap="round" strokeLinejoin="round" />
                  </>
                ) : isComeBackNextDay ? (
                  <>
                    <path d="M18 9V20" stroke="#d97706" strokeWidth="3.8" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="18" cy="26.5" r="2.3" fill="#d97706" />
                  </>
                ) : (
                  <path d="M10 18L16 24L26 14" stroke="#16a34a" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
                )}
              </svg>
            </div>
          </div>
          {!preview && (
            <button type="button" className="close-btn-inline" onClick={onClose || handleClose} aria-label="Close">
              ×
            </button>
          )}
        </div>

        <div className="success-badge" style={{ background: activeTheme.gradient }}>
          {activeTheme.badge}
        </div>

        {scanImage && (
          <img className="scan-header-image" src={scanImage} alt="Scan result" />
        )}

        {isHardBlocked && (
          <h1 className="success-title used-title" style={{ color: '#dc2626' }}>
            {USED_THEME.title}
          </h1>
        )}

        {isComeBackNextDay && (
          <h1 className="success-title" style={{ color: activeTheme.accent }}>
            {SCANNED_TODAY_THEME.title}
          </h1>
        )}

        {!isBlocked && !scanImage && (
          <h1 className="success-title" style={{ color: activeTheme.accent }}>
            {activeTheme.title}
          </h1>
        )}

        <div className="ticket-info" style={ticketInfoStyle}>
          <div className="info-row">
            <span className="info-label">Check-in</span>
            <span className="info-value">{formatDateTime(ticketData.checkInTime || new Date())}</span>
          </div>

          <div className="info-row">
            <span className="info-label">Pass Type</span>
            <span className="info-value highlight pass-type-value" style={{ color: isHardBlocked ? '#334155' : activeTheme.accent }}>
              {passType}
            </span>
          </div>

          {eventDateLabel && (
            <div className="info-row">
              <span className="info-label">Event Date</span>
              <span className="info-value">{eventDateLabel}</span>
            </div>
          )}

          {isVendorCompPass && (
            <div className="info-row">
              <span className="info-label">Allocation</span>
              <span className="info-value">2x 3-Day Passes</span>
            </div>
          )}

          {isHardBlocked && (
            <div className="info-row">
              <span className="info-label">Status</span>
              <span className="info-value used-status">Already Used</span>
            </div>
          )}

          {isHardBlocked && usedAtText && (
            <div className="info-row">
              <span className="info-label">Used At</span>
              <span className="info-value">{usedAtText}</span>
            </div>
          )}

          {isHardBlocked && ticketData.scannedBy && (
            <div className="info-row">
              <span className="info-label">Scanned By</span>
              <span className="info-value">{ticketData.scannedBy}</span>
            </div>
          )}

          {isHardBlocked && scannedAtText && (
            <div className="info-row">
              <span className="info-label">Scan Time</span>
              <span className="info-value">{scannedAtText}</span>
            </div>
          )}

          {isBlocked && ticketData.message && (
            <div className="info-row info-row-stack">
              <span className="info-label">Info</span>
              <span className="info-value info-value-full">{ticketData.message}</span>
            </div>
          )}

          {isAmp && ticketData.section && (
            <div className="info-row">
              <span className="info-label">Section</span>
              <span className="info-value highlight" style={{ color: isHardBlocked ? '#334155' : activeTheme.accent }}>{ticketData.section}</span>
            </div>
          )}
          {isAmp && (ticketData.row || ticketData.seat) && (
            <div className="info-row">
              <span className="info-label">Seat</span>
              <span className="info-value">Row {ticketData.row}, Seat {ticketData.seat}</span>
            </div>
          )}

          {ticketData.totalDays && ticketData.totalDays > 1 && (
            <div className="info-row">
              <span className="info-label">Day</span>
              <span className="info-value">Day {ticketData.dayNumber || '?'} of {ticketData.totalDays}</span>
            </div>
          )}

          {(ticketData.holderName || ticketData.ticketData?.owner_name) && (
            <div className="info-row">
              <span className="info-label">Holder</span>
              <span className="info-value">{ticketData.holderName || ticketData.ticketData?.owner_name}</span>
            </div>
          )}

          {ticketData.ownerEmail && (
            <div className="info-row">
              <span className="info-label">Email</span>
              <span className="info-value info-email" style={{ fontSize: 12 }}>{ticketData.ownerEmail}</span>
            </div>
          )}
        </div>

        {isAmp && ticketData.scanMode && ticketData.scanMode !== 'both' && (
          <div style={{
            margin: '0 auto 12px', padding: '6px 16px', borderRadius: 8,
            background: ticketData.scanMode === 'amphitheater' ? '#ede9fe' : '#ecfdf5',
            color: ticketData.scanMode === 'amphitheater' ? '#7c3aed' : '#059669',
            fontSize: 13, fontWeight: 700, textAlign: 'center',
          }}>
            {ticketData.scanMode === 'amphitheater' ? '🎵 Amphitheater Entry' : '🎪 Festival Day Entry'}
            {ticketData.ampScanned && ticketData.festivalScanned
              ? ' — Fully Scanned'
              : ticketData.ampScanned ? ' — Festival entry remaining'
              : ticketData.festivalScanned ? ' — Amphitheater entry remaining'
              : ''}
          </div>
        )}

        {!preview && (
          <div className="success-buttons">
            <button
              className="btn-scan-next"
              onClick={handleScanNext}
              style={{ background: activeTheme.gradient }}
            >
              Scan Next Ticket
            </button>
            <button
              className="btn-close"
              onClick={handleClose}
              style={{ color: activeTheme.accent }}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const TicketSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const ticketData = location.state || {};

  return (
    <TicketSuccessCard
      ticketData={ticketData}
      onScanNext={() => navigate('/scanner')}
      onClose={() => navigate('/scanner')}
    />
  );
};

export default TicketSuccess;
